/* @flow */


import bidbase, { INIT_STATUS } from './bidbase';
import { logError } from '../utils/logger';
import { extend, isObject, isFunction, isArray } from '../utils/coreutils';
import { getRTBUserEids } from '../idproviders/idputils';
import {
  PREBID_UID_PUBPROVIDEDID,
  PREBID_UID_PUBCOMMON,
  PREBID_UID_LIVERAMP,
  PREBID_UID_CRITEO,
  PREBID_UID_UNIFIEDID,
  PREBID_UID_ID5ID,
  PREBID_UID_PARRABLEID
} from '../idproviders/idpconstants';
import { getTopLevelDomain } from '../utils';

declare type criRespSlot = {
  impressionId: string,
  zoneId: number,
  cpm: number,
  id: string
};

declare type criResp = Array<criRespSlot>;

declare type criReqSlot = {
  zoneid: string,
  slotid: string
};

declare type criReq = {
  networkId: number,
  publisherId: string,
  placements: Array<criReqSlot>
};

interface hbCriObj {
  events: Array<cbFunc>;
  RequestBids(criReq, cbFunc, number): void;
  RenderAd(string, Document): void;
  GetBids(): criResp;
  SetIdentities(RTBEids): void;
}

/**
 * @module bidders/criteo
 * @requires module:logger
 */

/**
 * @typedef {object} hbPosData
 * @typedef {object} initStatus
 */

/**
 * @description Implements functionality for Criteo header bidding
 * @augments _bidbase
 * @requires module:utils
 */
class _criteobid extends bidbase {
  _config: criteoConfig;

  _DefConfig: criteoConfig;

  constructor(name: string = 'criteo') {
    super(name);
    this._DefConfig = extend(true, {}, this._DefConfig, {
      networkId: 0,
      publisherId: ''
    });
    this._mandatoryConfigs = ['script', 'publisherId'];
    this._posId = 'zoneid';
  }

  _getCriObj(): hbCriObj {
    return window.Criteo;
  }

  /**
   * Default implementation of _externalScriptCheck. If required bidder implementation should override this.
   *
   * @returns {initStatus} - Status
   * @access private
   */
  _externalScriptCheck(): initStatus {
    const cri = this._getCriObj();
    // $FlowFixMe
    if (isObject(cri) && isFunction(cri.RequestBids)) {
      return INIT_STATUS.F_INITED;
    }
    return INIT_STATUS.F_FAILED;
  }

  /**
   * Process bid response. Response Format:
   * res[slot].cpm - ecpm
   * res[slot].impressionId - pos
   * res[slot].zoneid - per position unique id
   *
   * @param {Array<string>} posList - array of positions
   * @param {object} res - Criteo response object
   * @param {object.<string, hbPosData>} ret - Return data
   * @returns {object.<string, hbPosData>} - Return data
   * @access private
   */
  _processBidResponse(
    posList: Array<string>,
    res: criResp,
    ret: hbPartnerData
  ): hbPartnerData {
    if (!isArray(res)) {
      return ret;
    }
    res.forEach(slot => {
      if (
        slot &&
        slot.impressionId &&
        this.getPosKey(slot.impressionId) === String(slot.zoneId) &&
        slot.cpm > 0 &&
        slot.id
      ) {
        this._handleBidPosData(
          ret,
          slot.impressionId,
          `${slot.cpm.toFixed(2)}`,
          slot.id
        );
      }
    });
    return ret;
  }

  /**
   * get bids for given positions
   *
   * @param {Array<string>} posList - HB position list
   * @param {object.<string, hbPosData>} ret - Return data
   * @returns {Promise<object.<string, hbPosData>>} - Promise
   * @access private
   */
  _getAds(posList: Array<string>, ret: hbPartnerData): Promise<hbPartnerData> {
    window.criteo_pubtag.standaloneBidder.bids = {};
    const { networkId = 0, publisherId = '' } = this._config;
    const adUnits: criReq = {
      networkId,
      publisherId,
      placements: posList.map(v => ({
        zoneid: this.getPosKey(v),
        slotid: v
      }))
    };

    const { user: { eids = [] } = {} } = getRTBUserEids([
      PREBID_UID_PUBPROVIDEDID,
      PREBID_UID_PUBCOMMON,
      PREBID_UID_LIVERAMP,
      PREBID_UID_CRITEO,
      PREBID_UID_UNIFIEDID,
      PREBID_UID_ID5ID,
      PREBID_UID_PARRABLEID
    ]);

    // update pubprovidedID as Criteo requires
    // Ref: https://publisherdocs.criteotilt.com/cdb/#hashed-emails
    const myDomain = getTopLevelDomain();
    const eids2 = eids.map(eid => {
      if (eid.source !== myDomain) {
        return eid;
      }
      const myEid = eid;
      myEid.uids = myEid.uids.map(uid => {
        const myUid = uid;
        if (uid.ext && uid.ext.stype === 'sha256email') {
          myUid.atype = 3;
          // $FlowFixMe[incompatible-use]
          myUid.ext.stype = 'hemsha256';
        }
        return myUid;
      });
      return eid;
    });
    return new Promise(resolve => {
      const cri = this._getCriObj();
      cri.events.push(() => {
        cri.SetIdentities(eids2);
        cri.RequestBids(
          adUnits,
          () => {
            resolve(this._processBidResponse(posList, cri.GetBids(), ret));
          },
          this._config.timeout
        );
      });
    });
  }

  /**
   * render ad for this partner
   *
   * @param {window} win - window object
   * @param {Document} doc - document object
   * @param {string} pos - ad position
   * @param {string} id - DOM ID
   * @param {module:adconfig.posConfigInfo} posInfo - Position information
   * @returns {undefined}
   */
  _renderAd(
    win: WindowProxy,
    doc: Document,
    pos: string,
    id: string,
    // eslint-disable-next-line no-unused-vars
    posInfo: posConfigInfo
  ) {
    try {
      const cri = this._getCriObj();
      cri.RenderAd(id, doc);
    } catch (error) {
      logError(`${this._name} ${pos} render ad failure ${error.message}`);
    }
  }
}

const criteobid: _criteobid = new _criteobid();
export default criteobid;
